import { useGetConcordNotesQuery } from '@/services/accounts';
import { Box, Button, Modal } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

interface AccountNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
}

function formatNoteDate(dateStr: string): string {
  return DateTime.fromISO(dateStr, { zone: 'utc' }).toFormat('LLLL d, yyyy');
}

export function AccountNotesModal({ isOpen, onClose, accountId }: AccountNotesModalProps) {
  const { data, isLoading, isError } = useGetConcordNotesQuery(accountId, {
    skip: !isOpen || !accountId,
  });

  const sortedNotes = useMemo(
    () => (data?.notes ?? []).toSorted((a, b) => b.date.localeCompare(a.date) || b.id - a.id),
    [data],
  );

  let content;
  if (isLoading) {
    content = (
      <Box as="p" fontSize="sm" color="body-secondary">
        Loading notes...
      </Box>
    );
  } else if (isError) {
    content = (
      <Box as="p" fontSize="sm" color="danger">
        Unable to load notes. Please try again.
      </Box>
    );
  } else if (sortedNotes.length === 0) {
    content = (
      <Box as="p" fontSize="sm" color="body-secondary">
        No notes
      </Box>
    );
  } else {
    content = sortedNotes.map((note) => (
      <Box key={note.id} borderWidth="xs" borderColor="separator" radius="md" padding="md" childGap="xs">
        <Box direction="row" justifyContent="space-between" gap="md">
          <Box as="p" fontSize="sm" fontWeight="bold">
            {formatNoteDate(note.date)}
          </Box>
          <Box as="p" fontSize="sm" color="body-secondary">
            {note.userName}
          </Box>
        </Box>
        <Box as="p" fontSize="sm" fontWeight="medium">
          {`${note.description} (${note.code})`}
        </Box>
        <Box as="p" fontSize="sm" data-testid="account-note-message">
          {note.message}
        </Box>
      </Box>
    ));
  }

  return (
    <Modal ariaLabelledBy="account-notes-title" isOpen={isOpen} onDismiss={onClose} maxWidth="680px">
      <Modal.Header id="account-notes-title" title="Notes" onDismiss={onClose} />
      <Modal.Body childGap="md">{content}</Modal.Body>
      <Modal.Footer>
        <Box direction="row" justifyContent="flex-end" width="100">
          <Button variant="primary" size="md" type="button" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
}
